import dayjs from "dayjs"
import { SPICE_INCOMPLETE_REMARK } from "../constants/create_ticket"
import { ProductMapping, ProductName } from "../enums/ProductMapping"
import {
  Service_Status,
  Service_Status_Code,
  STT_R2_Class_Matrix,
  STT_R2_Class_Matrix_Access_Failure,
  STT_R2_Class_Matrix_Connection_Failure,
} from "../enums/Service"
import { BillingDetails } from "../types/billingDetails.type"
import {
  CustomerAccount,
  RearrangedCustomerAccount,
} from "../types/customerDetails.type"
import {
  Product,
  ServiceAccount,
  ServiceAccountTaas,
  ServiceDetails,
} from "../types/serviceDetails.type"
import api from "../utils/api"

const convertUrl = (url: string) => {
  const newUrl = `${api[api.env].backendURL}/${url}`
  return newUrl
}

function accountDetail(
  arrangedCA: RearrangedCustomerAccount[],
  customerAcc: CustomerAccount[]
) {
  const filteredCustomerAcc: {
    AccountNumber: string
    EncryptedAccountNumber: string
  }[] = []
  //Push deep array bill account number in single array and return

  customerAcc.forEach(account => {
    if (account.BillingAccounts && account.BillingAccounts.length) {
      account.BillingAccounts.forEach(billAcc => {
        filteredCustomerAcc.push({
          AccountNumber: billAcc.AccountNumber,
          EncryptedAccountNumber: billAcc.EncryptedAccountNumber,
        })
      })
    }
  })
  //match account number for safe check
  const filteredArrangedCA = arrangedCA.map(ca => {
    const currentAcc = filteredCustomerAcc.find(
      elem => ca.accountNumber === elem.AccountNumber
    )
    if (currentAcc) {
      return {
        encryptedAccountNumber: currentAcc.EncryptedAccountNumber,
        systemName: ca.systemName,
        uca: ca,
      }
    }
    return false
  })
  //Filter those which have account number
  const newFilteredArrangedCA = filteredArrangedCA.filter(elem => {
    return elem != false
  })
  //customerAcc[0].BillingAccounts[0].EncryptedAccountNumber
  return newFilteredArrangedCA
}

const billingRearrange = async (
  billings: BillingDetails,
  ba: RearrangedCustomerAccount
) => {
  const newBA: any = { ...ba }
  if (billings.BillingHistory !== undefined) {
    newBA.latestBilling = billings.BillingHistory[0]
  } else {
    newBA.latestBilling = {}
  }

  if (billings.PaymentHistory !== undefined) {
    newBA.latestPayment = billings.PaymentHistory[0]
  } else {
    newBA.latestPayment = {}
  }

  if (billings.CreditUtilization !== undefined) {
    newBA.creditUtilization = billings.CreditUtilization
  } else {
    newBA.creditUtilization = {}
  }

  if (
    billings.LatestDueUnbilledUsage !== undefined &&
    billings.LatestDueUnbilledUsage !== ""
  ) {
    newBA.latestDueUnbilledUsage = billings.LatestDueUnbilledUsage
  } else {
    newBA.latestDueUnbilledUsage = {}
  }

  return ba
}

const serviceRearrange = async (
  service: ServiceDetails,
  sa: RearrangedCustomerAccount
) => {
  if (service.ServiceAccount !== undefined) {
    sa.serviceAccount = service.ServiceAccount
  } else {
    sa.serviceAccount = []
  }

  return sa
}

const CheckNetworkAlertStatus = (
  status: STT_R2_Class_Matrix_Access_Failure
): boolean => {
  return (
    Object.values(STT_R2_Class_Matrix_Access_Failure).findIndex(
      state => state === status
    ) > -1
  )
}
const CheckConnectionFailureStatus = (
  status: STT_R2_Class_Matrix_Connection_Failure
): boolean => {
  return (
    [
      STT_R2_Class_Matrix_Connection_Failure.OFFLINE_CPE_TROUBLESHOOT,
      STT_R2_Class_Matrix_Connection_Failure.OFFLINE_BAD_NEIGHBOUR,
      STT_R2_Class_Matrix_Connection_Failure.OFFLINE_BAD_NEIGHBOUR_FREQ_DC,
      STT_R2_Class_Matrix_Connection_Failure.OFFLINE_FREQ_DC,
    ].findIndex(state => state === status) > -1
  )
}

const checkProductMapping = (products: Product[]) => {
  const findInternet = products.findIndex(product => {
    return product.ProductName === ProductName.Internet || (product.SerialNumber.indexOf("@unifi") !== -1 || product.SerialNumber.indexOf("@unifibiz") !== -1)
  })
  const findVoice = products.findIndex(product => {
    return product.ProductName === ProductName.Voice || product.ProductName.indexOf("Voice") != -1 || product.ProductName.indexOf("voice") != -1
  })
  if (findInternet >= 0) return ProductMapping[ProductName.Internet]
  else if (findVoice >= 0) return ProductMapping[ProductName.Voice]
}

// The outage / NTT lookup keys on the broadband service id (name@unifi or
// name@unifibiz), which lives on the product, not on the service account. A
// bundled account reports ServiceNumber as "BUNDLE<rowid>", which the outage
// service cannot resolve.
const getBroadbandServiceNumber = (products: any[]): string | undefined => {
  const broadband = (products || []).find(
    (product: any) =>
      (product?.SerialNumber ?? "").indexOf("@unifi") !== -1 ||
      (product?.SerialNumber ?? "").indexOf("@unifibiz") !== -1
  )
  return broadband?.SerialNumber
}

// i-Engage answers an unparseable searchSpecByPeriod with a placeholder record
// rather than an error - every identifier and date empty, but Status "Open" and
// TicketType "TT":
//
//   {"SystemName":"ICP","SRNumber":"","TTNumber":"","CreatedDate":"",
//    "Status":"Open","TicketType":"TT", ...}
//
// Taken at face value that reads as an open ticket, which would stop every
// customer raising one. A record carrying no identifier and no created date
// cannot be a real ticket, so drop it before any status check.
//
// EasyFix sends MM/DD/YYYY, which is the format i-Engage expects, so this should
// never trigger today. It is here so a future locale change, dayjs upgrade or
// edit to the date handling degrades to "no tickets found" rather than to
// "everyone is blocked", which would be very hard to diagnose in production.
const hasTicketIdentity = (ticket: any): boolean => {
  const value = (v: any) => String(v ?? "").trim()
  return !!(
    value(ticket?.SRNumber) ||
    value(ticket?.TTNumber) ||
    value(ticket?.novaTTId) ||
    value(ticket?.CreatedDate)
  )
}

// A ticket counts as open when it is neither Cancelled nor Closed and was
// created within the last 30 days.
//
// The 30-day window is agreed business behaviour: a customer may raise a new
// ticket when the existing one is older than that. Both the diagnosis-time check
// and the re-check at submit share this function precisely so the two can never
// drift apart and start disagreeing about what counts as open.
const filterOpenTickets = (srDetails: any): any[] => {
  if (!Array.isArray(srDetails) || srDetails.length === 0) return []
  return srDetails.filter((ticket: any) => {
    if (dayjs(ticket?.CreatedDate).isBefore(dayjs().subtract(30, "days"))) {
      return false
    }
    return !!(
      ticket?.Status &&
      ticket.Status !== "Cancelled" &&
      ticket.Status !== "Closed"
    )
  })
}

const CAMELIA_MARKER = "[CAMELIA]:"
// The generic CSP agent script starts at this token inside expert.inbound.
const AGENT_SCRIPT_MARKER = "Inbound:"
// expert.inbound runs to roughly 4KB. Nothing we send should approach that, and
// the TaaS note field limit is still unconfirmed, so cap defensively.
const MAX_NOTE_LENGTH = 1000

// SPICE sometimes answers with a bare instruction to the agent rather than any
// fault information. In the 11-13 Sep case export, 100 of 636 tickets carried
// only "CSP to: Create CTT and CTT activity rno troubleshooting", which tells
// the field team nothing. Such a value is treated as absent so the chain below
// can fall through to something useful.
//
// Deliberately narrow: it matches the directive form only. The useful notes also
// begin with "CSP to" - "CSP to add remarks in CTT [CAMELIA]: ..." - and those
// must keep flowing through untouched.
const CSP_DIRECTIVE_PATTERNS = [/^CSP to:\s*create ctt\b/i]

const isBareCspDirective = (value: string): boolean =>
  CSP_DIRECTIVE_PATTERNS.some(pattern => pattern.test(value))

// The usable fault detail is the CAMELIA access-network remark, which sits
// inside expert.inbound between "[CAMELIA]:" and the "Inbound:" that begins the
// generic CSP agent script.
//
// Only that slice is taken. expert.inbound as a whole is about 4,200 characters
// of agent guidance - troubleshooting steps, product upsell wording, iNFERA
// links and HTML - none of which belongs on a field ticket.
//
// Where a diagnosis carries no CAMELIA remark either, expert.summary is used: it
// carries the measurements that were actually taken, e.g. "HSI Session OFFLINE.
// NE (MSAN) DOWN. BTU/ONU DOWN."
const getSpiceFaultRemark = (expert: any): string => {
  const collapse = (value: any): string =>
    String(value ?? "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()

  // A candidate is only worth putting on a ticket if it says something. The
  // bare-directive test has to be applied to every source, not just the first:
  // when SPICE produces no diagnosis it puts the directive in expert.summary,
  // and that reached the field team verbatim on two tickets in mid-September.
  const usable = (value: string): boolean => !!value && !isBareCspDirective(value)

  // The field the interface intends for this. It was empty in the August
  // captures but is populated again, and is what reaches the ticket today.
  const caselovNote = collapse(
    expert?.SRTT_Ticket_Recommendation?.result?.gated?.CASELOV?.note_description
  )
  if (usable(caselovNote)) {
    return caselovNote.slice(0, MAX_NOTE_LENGTH)
  }

  const inbound = collapse(expert?.inbound)
  const markerAt = inbound.indexOf(CAMELIA_MARKER)
  if (markerAt !== -1) {
    let remark = inbound.slice(markerAt + CAMELIA_MARKER.length)
    const scriptAt = remark.indexOf(AGENT_SCRIPT_MARKER)
    if (scriptAt !== -1) remark = remark.slice(0, scriptAt)
    remark = collapse(remark)
    if (usable(remark)) return remark.slice(0, MAX_NOTE_LENGTH)
  }

  const summary = collapse(expert?.summary)
  if (usable(summary)) return summary.slice(0, MAX_NOTE_LENGTH)

  // Nothing usable. Which of the two fallbacks applies depends on whether SPICE
  // answered at all: if any of the three sources carried text - even text we
  // rejected - then it did, and the note should say the diagnosis was
  // incomplete rather than assert readings nobody took.
  //
  // Returning "" means SPICE was silent, and hands the decision back to the
  // caller, which substitutes FIELD_TEAM_REMARK.
  const spiceAnswered = !!(caselovNote || inbound || summary)
  return spiceAnswered ? SPICE_INCOMPLETE_REMARK : ""
}

// Whether a service may be diagnosed, and if not, why we stop.
//
// The status arrives in two different shapes. NOVA sends the words in
// Service_Status ("Active", "Suspended"); TaaS sends a single letter, "A" or
// "M". The guards used to compare against the words only, so a TaaS service
// carrying "M" fell through as if it were healthy and the customer was allowed
// to run a diagnosis and raise a CTT on a suspended line.
//
// This is an allow-list on purpose: only a confirmed-active service proceeds.
// The full TaaS status-code map has been requested from the TaaS team; until it
// arrives, an unrecognised code is reported as inactive rather than assumed
// good.
type Service_Diagnosability = "active" | "suspended" | "inactive"

const ACTIVE_STATUSES: string[] = [
  Service_Status_Code.Active,
  Service_Status.Active.toUpperCase(),
]

const SUSPENDED_STATUSES: string[] = [
  Service_Status_Code.Suspended,
  Service_Status.Suspended.toUpperCase(),
  Service_Status.Dunning_Suspended.toUpperCase(),
]

const getServiceDiagnosability = (status: any): Service_Diagnosability => {
  const normalised = String(status ?? "")
    .trim()
    .toUpperCase()
  if (ACTIVE_STATUSES.indexOf(normalised) !== -1) return "active"
  if (SUSPENDED_STATUSES.indexOf(normalised) !== -1) return "suspended"
  return "inactive"
}

// The label shown on the service card, kept in step with the guard above so the
// screen cannot say one thing while the guard does another.
const getServiceStatusLabel = (status: any): string => {
  const diagnosability = getServiceDiagnosability(status)
  if (diagnosability === "active") return "Active"
  if (diagnosability === "suspended") return "Suspended"
  return "Inactive"
}

// Whether a response is an expired or rejected session rather than a real answer.
//
// The API proxy turns an upstream 401 into {"http_status_code":401,...} with no
// Status object at all. The open-ticket check read that as "no cases found" and
// let ticket creation proceed, so a customer whose token had expired could
// raise a duplicate of a case nobody had actually looked for. Observed in
// testing on 19 October.
//
// 900901/900902/900908 are the WSO2 gateway's own credential errors, included
// in case the proxy ever passes the upstream body through unchanged.
const isAuthFailure = (response: any): boolean => {
  if (!response) return false
  if (Number(response.http_status_code) === 401) return true
  const code = String(response.code ?? "")
  return code === "900901" || code === "900902" || code === "900908"
}

const getFormattedAddress = (account: ServiceAccount | ServiceAccountTaas) => {
  const formattedAddress = `

    ${account.UnitLot && `${account.UnitLot},`}
    ${account.FloorNo} 
    ${account.BuildingName} 
    ${account.StreetType} 
    ${account.StreetName} 
    ${account.UnitLot}, 
    ${account.Section}, 
    ${account.Postcode} 
    ${account.City},
    ${account.State}
  `
  return formattedAddress
}

function copyFormatted(html: string) {
  // Create container for the HTML
  // [1]
  const container = document.createElement("div")
  container.innerHTML = html

  // Hide element
  // [2]
  container.style.position = "fixed"
  container.style.pointerEvents = "none"
  container.style.opacity = "0"

  // Detect all style sheets of the page
  const activeSheets = Array.prototype.slice
    .call(document.styleSheets)
    .filter(sheet => !sheet.disabled)

  // Mount the container to the DOM to make `contentWindow` available
  // [3]
  document.body.appendChild(container)

  // Copy to clipboard
  // [4]
  if (typeof window) {
    window.getSelection().removeAllRanges()

    const range = document.createRange()
    range.selectNode(container)
    window.getSelection().addRange(range)

    // [5.1]
    document.execCommand("copy")

    // [5.2]
    for (let i = 0; i < activeSheets.length; i += 1) {
      activeSheets[i].disabled = true
    }

    // [5.3]
    document.execCommand("copy")

    // [5.4]
    for (let i = 0; i < activeSheets.length; i += 1) {
      activeSheets[i].disabled = false
    }

    // Remove the container
    // [6]
    document.body.removeChild(container)
  }
}

const calculateInternetSpeed = (
  packageData: string = "Unifi 0Mbps Speed"
): number => {
  const breakpoint = /\Gbps|Mbps|M/

  const data = packageData.match(breakpoint)
  let speed = 0

  if (data) {
    const packageType = data[0]
    const splitData = packageData.split(breakpoint)
    const newSplitData = splitData[0].split(" ")
    const speedData = newSplitData[newSplitData.length - 1]
    speed = parseInt(speedData, 10)

    if (packageType === "Gbps") {
      speed = parseInt(speedData, 10) * 1024
    }
  }
  if (speed != undefined && !isNaN(speed)) {
    return speed
  }
  return 0
}

const getFormattedDateDetails = () => {
  const date = new Date()
  const year = date.getFullYear().toString().substr(-2)
  const month = date.getMonth() + 1
  const formattedMonth = month > 9 ? `${month}` : `0${month}`
  const day = date.getDate()
  const formattedDay = day > 9 ? `${day}` : `0${day}`
  const formattedHour =
    date.getHours() > 9 ? `${date.getHours()}` : `0${date.getHours()}`
  const formattedMinute =
    date.getMinutes() > 9 ? `${date.getMinutes()}` : `0${date.getMinutes()}`
  const formattedSec =
    date.getSeconds() > 9 ? `${date.getSeconds()}` : `0${date.getSeconds()}`

  return `${year}${formattedMonth}${formattedDay}${formattedHour}${formattedMinute}${formattedSec}`
}

export {
  convertUrl,
  accountDetail,
  billingRearrange,
  serviceRearrange,
  checkProductMapping,
  filterOpenTickets,
  hasTicketIdentity,
  getBroadbandServiceNumber,
  isAuthFailure,
  getServiceDiagnosability,
  getServiceStatusLabel,
  getSpiceFaultRemark,
  getFormattedAddress,
  CheckConnectionFailureStatus,
  CheckNetworkAlertStatus,
  copyFormatted,
  calculateInternetSpeed,
  getFormattedDateDetails,
}
